import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { CommanServices } from '@/services/commanServices';
import { Boxes, Plus, Pencil, Trash2, Repeat, Clock, Hash, Search } from 'lucide-react';
import { toast } from 'sonner';
import ResourceFormModal from '../components/ResourceFormModal';
import {
  AVAILABILITY, num, money, unitLabel, blankResource,
  catalogToResourceForm, resourceFormToCatalogPayload,
} from '../components/resourceInject';

const AVAILABILITY_ICONS = { once: Hash, reuse: Repeat, time: Clock };

// Resources reference module — global catalog of resource types.
// Anything saved here can be picked when adding a resource inject to an
// incident/exercise, and resources created from that inject modal land here.
const Resources = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [draft, setDraft] = useState(null); // form-shaped object for ResourceFormModal

  const load = useCallback(async () => {
    try {
      const res = await CommanServices.getResourcesCatalog();
      setItems(res?.status && Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return items;
    return items.filter((c) => String(c.name || '').toLowerCase().includes(q));
  }, [items, searchTerm]);

  const grandTotal = useMemo(
    () => filtered.reduce((sum, c) => sum + num(c.quantity) * num(c.unit_cost), 0),
    [filtered]
  );

  const save = async (form) => {
    if (!form.category?.trim()) { toast.error('Resource type (category) is required'); return; }
    const payload = resourceFormToCatalogPayload(form);
    const res = form.id
      ? await CommanServices.updateResourceCatalog(form.id, payload)
      : await CommanServices.addResourceCatalog(payload);
    if (!res?.status) {
      toast.error(res?.message || 'Failed to save resource');
      return;
    }
    setDraft(null);
    toast.success(form.id ? 'Resource updated' : 'Resource added');
    await load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete resource "${c.name}"? Existing incident injects keep their own copy.`)) return;
    const res = await CommanServices.deleteResourceCatalog(c.id);
    if (!res?.status) { toast.error(res?.message || 'Failed to delete resource'); return; }
    toast.success('Resource deleted');
    await load();
  };

  return (
    <div className="h-screen bg-gray-50 flex flex-1 overflow-hidden">
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Resources</h2>
          <p className="text-sm text-gray-600 mb-4">
            Reference catalog of resource types (vehicles, equipment, supplies). Resources saved here can be
            selected when adding a resource inject to an incident or exercise — and new resources created there
            are saved back into this catalog.
          </p>

          <div className="flex items-center justify-between gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                data-testid="resources-search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search resources…"
                className="w-full text-sm border border-gray-300 rounded-lg bg-white pl-9 pr-3 py-2"
              />
            </div>
            <button
              data-testid="catalog-add-resource-btn"
              onClick={() => setDraft(blankResource())}
              className="flex items-center gap-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold px-3.5 py-2.5 hover:bg-indigo-700 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" /> Add Resource
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="resources-catalog-panel">
          <div className="flex items-center gap-2.5 px-5 py-3.5 border-b bg-slate-50/60">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white">
              <Boxes className="h-4 w-4" />
            </span>
            <div>
              <h3 className="text-sm font-bold text-gray-900 leading-tight">Resource Catalog</h3>
              <p className="text-[11px] text-gray-500">{items.length} resource type{items.length === 1 ? '' : 's'} defined</p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-10 text-sm text-gray-400" data-testid="catalog-loading">Loading resources…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12" data-testid="catalog-empty">
              <Boxes className="h-9 w-9 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500 font-medium">
                {searchTerm ? 'No resources match your search' : 'No resources defined yet'}
              </p>
              {!searchTerm && (
                <p className="text-xs text-gray-400 mt-1">
                  Add resource types here so they can be picked when adding a resource inject to an incident.
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="catalog-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-gray-400 bg-gray-50/70 border-b">
                    <th className="text-left font-bold px-4 py-2.5">Resource Type / Category</th>
                    <th className="text-center font-bold px-3 py-2.5">Quantity</th>
                    <th className="text-left font-bold px-3 py-2.5">Availability</th>
                    <th className="text-right font-bold px-3 py-2.5">Unit Cost</th>
                    <th className="text-right font-bold px-3 py-2.5">Total Cost</th>
                    <th className="px-3 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((c) => {
                    const av = AVAILABILITY[c.availability] || AVAILABILITY.once;
                    const AvIcon = AVAILABILITY_ICONS[c.availability] || Hash;
                    return (
                      <tr key={c.id} className="hover:bg-slate-50/60" data-testid={`catalog-row-${c.id}`}>
                        <td className="px-4 py-3 font-semibold text-gray-900">{c.name}</td>
                        <td className="px-3 py-3 text-center font-bold text-gray-700">{num(c.quantity)}</td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${av.color}`}>
                            <AvIcon className="h-3 w-3" /> {av.label}
                            {c.availability === 'time' && c.replenish_every
                              ? <span className="font-normal opacity-80">· every {c.replenish_every} {unitLabel(c.replenish_unit)}</span>
                              : null}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right text-gray-600">{money(c.unit_cost)}</td>
                        <td className="px-3 py-3 text-right font-bold text-gray-900">{money(num(c.quantity) * num(c.unit_cost))}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              data-testid={`catalog-edit-${c.id}`}
                              onClick={() => setDraft({ ...catalogToResourceForm(c), id: c.id })}
                              className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-indigo-600"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              data-testid={`catalog-delete-${c.id}`}
                              onClick={() => remove(c)}
                              className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-rose-600"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 bg-slate-50/70">
                    <td className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500" colSpan={4}>Total Cost</td>
                    <td className="px-3 py-3 text-right text-base font-black text-indigo-700" data-testid="catalog-grand-total">{money(grandTotal)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      {draft && (
        <ResourceFormModal draft={draft} catalogMode onSave={save} onClose={() => setDraft(null)} />
      )}
    </div>
  );
};

export default Resources;
